import { graphql } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError';

import type {
	useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$data,
	useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$variables,
} from './__generated__/useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery.graphql';

type UseOrgDefaultClassificationLevelIdProps = {
	skip?: boolean;
};

type UseOrgDefaultClassificationLevelIdType = {
	orgDefaultClassificationLevelId: string | null;
	orgDefaultClasificationLoading: boolean;
	orgDefaultClassificationError?: ApolloErrorInterop;
};

const shouldMarkErrorAsHandled = (error?: ApolloErrorInterop) => {
	if (!error) return true;
	const statusCode = getStatusCode(error?.graphQLErrors?.[0]);
	if (!statusCode) return true;
	return !!(parseInt(statusCode) >= 400 && parseInt(statusCode) < 500);
};

export const useOrgDefaultClassificationLevelId = ({
	skip,
}: UseOrgDefaultClassificationLevelIdProps): UseOrgDefaultClassificationLevelIdType => {
	const { data, loading, error } = useQueryInterop<
		useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$data,
		useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$variables
	>(
		graphql`
			query useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery {
				orgDefaultClassificationLevelId
			}
		`,
		{ skip },
	);

	if (shouldMarkErrorAsHandled(error)) {
		markErrorAsHandled(error);
	}

	const orgDefaultClassificationLevelId = data?.orgDefaultClassificationLevelId ?? null;

	return {
		orgDefaultClassificationLevelId,
		orgDefaultClasificationLoading: loading,
		orgDefaultClassificationError: !shouldMarkErrorAsHandled(error) ? error : undefined,
	};
};
